/**
 * Macro #20 §1 — server-authoritative admin capability.
 *
 * Reads `GET /api/v1/me/capabilities`. The authority is the SIWE SESSION (never
 * the connected wallet, never localStorage, never a client flag) checked against
 * the env allowlist server-side. This hook only gates the PRESENCE of the
 * `[ ADMIN ]` HUD affordance — every admin API independently re-enforces
 * `require_admin`. Fail-closed: no session / not allow-listed / any error → not
 * admin. Re-checks when the connected wallet changes (a likely session change).
 */
import { useEffect, useRef, useState } from "react";
import { useAccount } from "wagmi";
import { moderationApi, ModerationApiError } from "../lib/moderation-api";

// Safety re-validation cadence. Only fires while an ADMIN affordance is live and
// the tab is visible, so a session that silently drops admin (logout / expiry /
// identity swap) fails closed even with no navigation or focus event.
const REVALIDATE_MS = 4000;

export function useAdminCapability(): { isAdmin: boolean; loading: boolean } {
  const { address } = useAccount();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const isAdminRef = useRef(false);
  isAdminRef.current = isAdmin;

  useEffect(() => {
    let cancelled = false;
    const controllers: AbortController[] = [];

    // Fail-closed re-validation of the SERVER capability. Runs on mount, on wallet
    // change, on tab focus / visibility / bfcache restore, on any hash/history
    // navigation, and on a bounded safety poll while elevated. Any error (incl. a
    // swap to a non-admin session) resolves to NOT admin. A single transient 401
    // (cold session hydration) is retried once. Background re-checks never toggle
    // `loading`, so a still-admin session gets no "Verifying access…" flash.
    const run = async (foreground: boolean) => {
      const controller = new AbortController();
      controllers.push(controller);
      if (foreground) setLoading(true);
      try {
        let c;
        try {
          c = await moderationApi.capabilities(controller.signal);
        } catch (e) {
          if (!cancelled && e instanceof ModerationApiError && e.status === 401) {
            await new Promise((r) => setTimeout(r, 400));
            c = await moderationApi.capabilities(controller.signal);
          } else {
            throw e;
          }
        }
        if (cancelled || controller.signal.aborted) return;
        setIsAdmin(!!c.is_admin);
      } catch {
        if (cancelled || controller.signal.aborted) return;
        setIsAdmin(false); // fail-closed
      } finally {
        if (!cancelled && !controller.signal.aborted && foreground) setLoading(false);
      }
    };

    run(true);

    const bg = () => { if (!cancelled) void run(false); };
    const onVisible = () => { if (!cancelled && document.visibilityState === "visible") void run(false); };
    window.addEventListener("focus", bg);
    window.addEventListener("pageshow", bg);
    window.addEventListener("hashchange", bg);
    window.addEventListener("popstate", bg);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      if (!cancelled && isAdminRef.current && document.visibilityState !== "hidden") void run(false);
    }, REVALIDATE_MS);

    return () => {
      cancelled = true;
      controllers.forEach((c) => c.abort());
      window.removeEventListener("focus", bg);
      window.removeEventListener("pageshow", bg);
      window.removeEventListener("hashchange", bg);
      window.removeEventListener("popstate", bg);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [address]);

  return { isAdmin, loading };
}
